/**
 * F 233 — the vault: seal a node, open a node, lock, unlock.
 *
 * WHAT THIS IS
 * ------------
 * `encryptNode` replaces an entity's plaintext `content` with ciphertext and
 * sets `encrypted: true`. `decryptNode` reverses it. `unlockVault` /
 * `lockVault` manage the session key that makes decryption possible at all.
 *
 * WHY A SESSION KEY AND NOT THE PASSPHRISE
 * ----------------------------------------
 * The store deliberately never holds a passphrase: "The store never derives a
 * key itself: the CALLER supplies one, already derived" (F 146 B). The vault
 * keeps that property. The passphrase is used ONCE, to derive a key; the key
 * is held in module state for the session and dropped on `lockVault()`. A
 * passphrase never reaches storage, and the session key never does either.
 *
 * THE CRYPTO IS NOT INVENTED HERE
 * -------------------------------
 * AES-GCM + the existing KDF, via `src/crypto.ts` (F 146 A), which is the same
 * path `freshcards/credentials` already uses and the same primitives the
 * fc-encryption module seals with. The record carries the KDF that actually
 * ran, so a value sealed with Argon2id and read on a host without Argon2id
 * fails loudly instead of silently decrypting to garbage.
 *
 * THE ONE THING THIS MODULE REFUSES TO DO
 * ---------------------------------------
 * It never returns plaintext to a caller that did not unlock the vault. The
 * AI read path depends on that being structural rather than a convention, so
 * it is enforced here once and every read goes through it.
 */

import { encrypt, decrypt } from '../../crypto';

// ─── The node surface this module operates on ─────────────────────────────
// Deliberately structural, not `BaseEntity`: the content field and the adapter
// differ per kind, and importing the whole entity model here would make the
// vault depend on the tree rather than the other way round.

export interface EncryptedNode {
  id: string;
  content?: unknown;
  encrypted?: boolean;
  anchors?: unknown[];
  [key: string]: unknown;
}

/** The ciphertext envelope stored in `content` when `encrypted` is true. */
export interface CipherEnvelope {
  /** Literal tag so a ciphertext blob is identifiable without the flag. */
  __fcCipher: 1;
  ciphertext: string;
  iv: string;
  salt: string;
  algorithm: string;
  kdf: string;
  /** The id this was sealed from, so a blob cannot be replayed onto another node. */
  nodeId: string;
}

export type Result<T = void> =
  | { ok: true; value: T }
  | { ok: false; error: string };

const ok = <T,>(value: T): Result<T> => ({ ok: true, value });
const fail = (error: string): Result<never> => ({ ok: false, error });

export function isCipherEnvelope(v: unknown): v is CipherEnvelope {
  return !!v && typeof v === 'object' && (v as any).__fcCipher === 1;
}

// ─── The session vault ────────────────────────────────────────────────────

let sessionKey: string | null = null;

/**
 * Unlock the vault for this session.
 *
 * The passphrase is verified against a real credential, so a wrong passphrase
 * is rejected here rather than surfacing later as a decryption failure on some
 * unrelated node. That ordering matters: "your passphrase is wrong" is a much
 * better answer than "this one record failed to decrypt" thirty nodes later.
 */
export async function unlockVault(passphrase: string, verify: VerifyPassphrase): Promise<Result<boolean>> {
  if (typeof passphrase !== 'string' || passphrase === '') {
    return fail('a passphrase is required');
  }
  try {
    const okVerify = await verify(passphrase);
    if (!okVerify) return fail('passphrase rejected');
    sessionKey = passphrase;
    return ok(true);
  } catch (err) {
    return fail(err instanceof Error ? err.message : String(err));
  }
}

export type VerifyPassphrase = (passphrase: string) => Promise<boolean> | boolean;

/** Drop the session key. After this, nothing decrypts until unlock again. */
export function lockVault(): Result<boolean> {
  const was = sessionKey !== null;
  sessionKey = null;
  return ok(was);
}

export function isVaultUnlocked(): boolean {
  return sessionKey !== null;
}

/** Test/host affordance. Never call this to "unlock" — it grants no key. */
export function _resetVaultForTests(): void {
  sessionKey = null;
}

// ─── Seal / open ──────────────────────────────────────────────────────────

export interface NodeStore {
  get(id: string): Promise<EncryptedNode | undefined>;
  set(id: string, node: EncryptedNode): Promise<void>;
}

/**
 * The node store the vault reads and writes. Configured once by the host.
 *
 * It is a dependency rather than a trailing argument so the public signatures
 * stay exactly what the model specifies — `encryptNode(entityId, passphrase)` —
 * instead of growing a `store` parameter that every caller would have to pass
 * and every future signature change would have to thread through.
 */
let nodeStore: NodeStore | null = null;

export function setNodeStore(store: NodeStore | null): void {
  nodeStore = store;
}

function requireStore(): NodeStore {
  if (!nodeStore) {
    throw new Error('[vault] no node store configured — call setNodeStore() first');
  }
  return nodeStore;
}

function serialise(v: unknown): string {
  return typeof v === 'string' ? v : JSON.stringify(v);
}

function deserialise(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
}

/**
 * Seal a node: encrypt its content, set `encrypted: true`, persist.
 *
 * The plaintext is NOT retained anywhere — not in the returned value, not in a
 * field, not in an undo buffer. A caller that wants to keep it must keep it.
 *
 * F 285 — `store` is a parameter, LAST, in the same position `setAnchors`
 * already uses. It is optional only because F 233's 22 existing call sites pass
 * two arguments; it defaults to the module store so nothing regresses. What it
 * does NOT do is stay bound to that one backend: a caller can now seal into a
 * store of its own choosing, and two consumers in one process no longer have to
 * agree on a global.
 */
export async function encryptNode(
  nodeId: string,
  passphrase: string,
  store: NodeStore = requireStore(),
): Promise<Result<CipherEnvelope>> {
  if (!nodeId) return fail('nodeId is required');
  if (!passphrase) return fail('a passphrase is required');
  const node = await store.get(nodeId);
  if (!node) return fail(`no node with id "${nodeId}"`);

  if (node.encrypted === true) {
    return fail(`node "${nodeId}" is already encrypted`);
  }
  if (node.content === undefined || node.content === null) {
    return fail(`node "${nodeId}" has no content to encrypt`);
  }

  try {
    const sealed = await encrypt(serialise(node.content), passphrase);
    const env: CipherEnvelope = {
      __fcCipher: 1,
      ciphertext: sealed.ciphertext,
      iv: sealed.iv,
      salt: sealed.salt,
      algorithm: sealed.algorithm,
      kdf: sealed.kdf,
      nodeId,
    };
    // STRUCTURE IS LEFT ALONE. id, parentId, order, edge, kind and anchors are
    // carried through untouched — a sealed node must still be traversable, or
    // it is not encrypted, it is lost.
    const next: EncryptedNode = { ...node, content: env, encrypted: true };
    await store.set(nodeId, next);
    return ok(env);
  } catch (err) {
    return fail(err instanceof Error ? err.message : String(err));
  }
}

/**
 * Open a node: replace its ciphertext with plaintext, and PERSIST that.
 *
 * Uses the SESSION key when the vault is unlocked, so an explicit passphrase is
 * not required for every read. A node that is not encrypted is returned
 * unchanged rather than refused.
 *
 * F 285 — this now WRITES. F 233 returned the plaintext and left the node
 * sealed, so a caller that trusted the return value showed "decrypted" while
 * the record was still ciphertext; the toggle lied and the next render flipped
 * it back. Encrypt has always persisted, so decrypt persisting is the symmetric
 * half, not a new behaviour. Chosen over returning "a result the caller writes
 * back" because that contract was already load-bearing in exactly one place —
 * the panel — and that one place was the bug. The write happens BEFORE the
 * success is returned, so a caller that gets `ok` is holding a record that is
 * already on disk, and a failed write is a failed call rather than a silent lie.
 *
 * Structure is carried through untouched, for the same reason `encryptNode`
 * does it: a node that lost its anchors, parent or order did not decrypt, it
 * vanished.
 */
export async function decryptNode(
  nodeId: string,
  passphrase?: string,
  store: NodeStore = requireStore(),
): Promise<Result<unknown>> {
  const key = passphrase ?? sessionKey;
  if (!key) return fail('vault is locked');
  const node = await store.get(nodeId);
  if (!node) return fail(`no node with id "${nodeId}"`);
  if (node.encrypted !== true) return ok(node.content);
  if (!isCipherEnvelope(node.content)) {
    return fail(`node "${nodeId}" is marked encrypted but its content is not a ciphertext envelope`);
  }
  const env = node.content;
  if (env.nodeId !== nodeId) {
    // A blob moved onto a different node. Refusing is the whole point of
    // stamping it — a ciphertext that decrypts anywhere is a ciphertext that
    // was copied somewhere it should not be.
    return fail(`ciphertext on "${nodeId}" was sealed from "${env.nodeId}"`);
  }
  try {
    const plain = await decrypt(env.ciphertext, env.iv, env.salt, key);
    const value = deserialise(plain);
    await store.set(nodeId, { ...node, content: value, encrypted: false });
    return ok(value);
  } catch (err) {
    return fail(err instanceof Error ? err.message : String(err));
  }
}

// ─── The read path ────────────────────────────────────────────────────────

export interface ReadNodeResult {
  /** The node, ALWAYS — structure is plaintext whether locked or not. */
  node: EncryptedNode;
  /** Plaintext content when the vault is open; the envelope when it is not. */
  content: unknown;
  /** True when the caller received ciphertext rather than plaintext. */
  locked: boolean;
  /** True only when a decrypt was attempted and FAILED. */
  error?: string;
}

/**
 * F 233 Stage 5 — read a node, transparently opening it when the vault allows.
 *
 * THE THREE CASES, all of which are normal:
 *   unlocked + encrypted → plaintext, `locked: false`
 *   locked   + encrypted → ciphertext envelope, `locked: true`
 *   not encrypted        → plaintext, `locked: false` (there is nothing to open)
 *
 * Structure is returned untouched in every case. A locked read of an encrypted
 * node is a USEFUL answer, not a failure: the caller can see the node exists,
 * where it sits in the tree, and what its anchors say.
 *
 * A decrypt that FAILS is distinct from a vault that is merely locked, and the
 * two are not conflated: `locked: false, error: '…'` means the key was wrong.
 */
export async function readNode(nodeId: string): Promise<ReadNodeResult> {
  const node = await requireStore().get(nodeId);
  if (!node) {
    return { node: { id: nodeId }, content: undefined, locked: true, error: `no node with id "${nodeId}"` };
  }
  if (node.encrypted !== true) {
    return { node, content: node.content, locked: false };
  }
  if (!isVaultUnlocked()) {
    // Structural, not advisory: there is no call to make, because there is no
    // key in this process. An AI holding this result cannot read the content.
    return { node, content: node.content, locked: true };
  }
  const opened = await decryptNode(nodeId);
  if (!opened.ok) {
    return { node, content: node.content, locked: false, error: (opened as { error: string }).error };
  }
  return { node: { ...node, content: opened.value }, content: opened.value, locked: false };
}

// ─── The AI read path ─────────────────────────────────────────────────────

export interface AiSearchHit {
  id: string;
  /** Anchor text that matched. The ONLY content-adjacent field on a hit. */
  matched: string;
  anchors: unknown[];
}

export interface AiSearchOptions {
  /**
   * Opt-in, per session, set only when the USER issues a specific query and
   * the vault is unlocked. Off by default and not persisted.
   */
  allowPlaintextWhenUnlocked?: boolean;
}

/**
 * F 233 Stage 6 — the AI's only door.
 *
 * It searches ANCHORS. There is no parameter that makes it read `content`,
 * because it never touches `content`. That is the point: "the AI cannot read
 * encrypted content" is only true if it is a property of the code, not of a
 * flag somebody might set.
 *
 * When the vault is unlocked AND the caller opted in, plaintext matching is
 * additionally available — and it is still NOT an `anchors`-only result, which
 * is why the caller has to ask for it by name.
 */
export async function aiSearch(
  query: string,
  nodes: readonly EncryptedNode[],
  opts: AiSearchOptions = {},
): Promise<AiSearchHit[]> {
  if (typeof query !== 'string' || query.trim() === '') return [];
  const q = query.toLowerCase();
  const hits: AiSearchHit[] = [];

  for (const n of nodes) {
    const anchors = (Array.isArray(n.anchors) ? n.anchors : []) as Array<{ text?: unknown }>;
    const matchedAnchors = anchors.filter((a) =>
      typeof a?.text === 'string' && String(a.text).toLowerCase().includes(q));

    if (matchedAnchors.length > 0) {
      hits.push({ id: n.id, matched: String(matchedAnchors[0].text), anchors: n.anchors });
      continue;
    }

    if (opts.allowPlaintextWhenUnlocked && isVaultUnlocked() && n.encrypted === true) {
      const opened = await decryptNode(n.id);
      if (opened.ok && serialise(opened.value).toLowerCase().includes(q)) {
        hits.push({ id: n.id, matched: '[plaintext: vault unlocked, opt-in]', anchors });
      }
    }
  }
  return hits;
}
